<div class="title-bar title-bar--responsive">
  <div class="title-bar__heading">
    <h1>DASHBOARD</h1>
    <app-help screen="dashboard"></app-help>
  </div>
</div>

@if (auth.user | async) {
  <div class="dashboard-page">
    @let status = status$ | async;

    <div class="dashboard-top-row" [class.dashboard-top-row--no-status]="!status">
      @if (status?.hasUnpublished) {
        <section class="status-banner status-banner--alert" aria-labelledby="status-unpublished-title">
          <h2 id="status-unpublished-title" class="status-banner__title">Review and publish</h2>
          <p class="status-banner__text">You have draft changes that aren't live yet.</p>
          <p class="status-banner__meta">
            @if (status.draftUpdated) {
              <span>Draft updated {{ status.draftUpdated | date: "mediumDate" }}</span>
            }
            <span>Last published {{ status.publishedDate | date: "mediumDate" }}</span>
          </p>
          <a mat-flat-button color="primary" routerLink="../inspector">Review in Inspector</a>
        </section>
      } @else if (status?.isBehind) {
        <section class="status-banner status-banner--info" aria-labelledby="status-behind-title">
          <h2 id="status-behind-title" class="status-banner__title">Update for {{ status.currentLabel }}</h2>
          <p class="status-banner__text">
            Your live calculator is still on {{ status.publishedLabel }}.
          </p>
          <p class="status-banner__text">Start with costs and awards below.</p>
        </section>
      } @else if (status?.isUpToDate) {
        <section class="status-banner status-banner--ok" aria-labelledby="status-up-to-date-title">
          <h2 id="status-up-to-date-title" class="status-banner__title">You're up to date</h2>
          <p class="status-banner__text">Your calculator is published for {{ status.publishedLabel }}.</p>
          <p class="status-banner__meta">
            <span>Published {{ status.publishedDate | date: "mediumDate" }}</span>
          </p>
        </section>
      }

      <section class="dashboard-top-row__activity" aria-labelledby="calculator-activity-title">
        <div class="activity-header">
          <h2 id="calculator-activity-title" class="dashboard-section__title">Calculator activity</h2>
          @if (activityMetric$ | async; as metric) {
            <div class="activity-metric-toggle" role="group" aria-label="Activity metric">
              <button
                type="button"
                class="activity-metric-toggle__btn"
                [class.activity-metric-toggle__btn--active]="metric === 'results'"
                [attr.aria-pressed]="metric === 'results'"
                (click)="setActivityMetric('results')"
              >
                Results
              </button>
              <button
                type="button"
                class="activity-metric-toggle__btn"
                [class.activity-metric-toggle__btn--active]="metric === 'visitors'"
                [attr.aria-pressed]="metric === 'visitors'"
                (click)="setActivityMetric('visitors')"
              >
                Visitors
              </button>
            </div>
          }
        </div>
        <mat-card appearance="outlined" class="activity-card">
          <mat-card-content>
            @if (activity$ | async; as activity) {
              <div class="activity-card__body">
                <div class="activity-card__stats">
                  <p class="activity-card__eyebrow">{{ activity.monthLabel }}</p>
                  <p class="activity-card__hero">
                    <span class="activity-card__hero-value">{{ activity.value | number }}</span>
                    {{ activity.unit }}
                  </p>
                  <p class="activity-card__meta">
                    @if (activity.vsPriorLabel) {
                      {{ activity.vsPriorLabel }} ·
                    }
                    {{ activity.completionPct }}% completion
                  </p>
                  <a class="activity-card__link" routerLink="../traffic">View traffic data ›</a>
                </div>
                <div
                  class="activity-spark"
                  role="img"
                  [attr.aria-label]="
                    activity.monthLabel + ': ' + activity.value + ' ' + activity.unit
                  "
                >
                  @for (bar of activity.bars; track bar.label) {
                    <div class="activity-spark__col">
                      <div
                        class="activity-spark__bar"
                        [class.activity-spark__bar--latest]="bar.isLatest"
                        [style.height.%]="bar.heightPct"
                        [attr.title]="bar.label + ': ' + bar.value"
                      ></div>
                      <span class="activity-spark__label">{{ bar.label }}</span>
                    </div>
                  }
                </div>
              </div>
            } @else {
              <p class="activity-card__empty">Loading traffic data…</p>
            }
          </mat-card-content>
        </mat-card>
      </section>
    </div>

    <section id="annual-updates" class="dashboard-section">
      @let usesIm = usesInstitutionalMethod$ | async;
      @if (status?.isBehind) {
        <h2 class="dashboard-section__title">Prepare for {{ status.currentLabel }}</h2>
        @if (status.hasUnpublished) {
          <p class="dashboard-section__subtitle">
            Your live calculator is still on {{ status.publishedLabel }}.
          </p>
        }
        <p class="dashboard-section__subtitle">
          @if (usesIm) {
            Update costs, awards, your grant aid disclosure, and IM options below, then publish when ready.
          } @else {
            Update costs, awards, and your grant aid disclosure below, then publish when ready.
          }
        </p>
      } @else {
        <h2 class="dashboard-section__title">Annual updates</h2>
        <p class="dashboard-section__subtitle">
          @if (usesIm) {
            Most schools review costs, awards, their grant aid disclosure, and IM options when preparing for a new aid year.
          } @else {
            Most schools review costs, awards, and their grant aid disclosure when preparing for a new aid year.
          }
        </p>
      }

      <div class="seasonal-cards">
        @for (card of seasonalCards; track card.path) {
          <mat-card appearance="outlined" class="seasonal-card">
            <mat-card-content>
              <div class="seasonal-card__row">
                <img class="seasonal-card__icon" [src]="card.icon" alt="" aria-hidden="true" />
                <div class="seasonal-card__content">
                  <h3 class="seasonal-card__title">{{ card.title }}</h3>
                  <p class="seasonal-card__text">{{ card.text }}</p>
                  <a mat-stroked-button color="primary" [routerLink]="card.path">
                    {{ card.action }}
                  </a>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        }
        @if (usesIm) {
          <mat-card appearance="outlined" class="seasonal-card">
            <mat-card-content>
              <div class="seasonal-card__row">
                <img class="seasonal-card__icon" [src]="needAnalysisCard.icon" alt="" aria-hidden="true" />
                <div class="seasonal-card__content">
                  <h3 class="seasonal-card__title">{{ needAnalysisCard.title }}</h3>
                  <p class="seasonal-card__text">{{ needAnalysisCard.text }}</p>
                  <a
                    mat-stroked-button
                    color="primary"
                    [href]="needAnalysisCard.href"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ needAnalysisCard.action }}
                  </a>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        }
      </div>

      <app-cohort-phrase-card></app-cohort-phrase-card>
    </section>

    <section class="dashboard-section">
      <h2 class="dashboard-section__title">Related setup</h2>
      <p class="dashboard-section__subtitle">If your awards depend on student answers or custom rules, also review:</p>
      <div class="tile-grid">
        @for (link of relatedLinks; track link.path) {
          <a class="ref-tile" [routerLink]="link.path">
            <span class="ref-tile__body">
              <span class="ref-tile__title">{{ link.title }}</span>
              <span class="ref-tile__subtitle">{{ link.subtitle }}</span>
            </span>
            <span class="ref-tile__chevron" aria-hidden="true">›</span>
          </a>
        }
      </div>
    </section>

    <section class="dashboard-section dashboard-section--quiet">
      <h2 class="dashboard-section__title">Personalization</h2>
      <p class="dashboard-section__subtitle">Usually reviewed only when your school presentation changes.</p>
      <div class="tile-grid tile-grid--two">
        @for (link of personalizationLinks; track link.path) {
          <a class="ref-tile ref-tile--quiet" [routerLink]="link.path">
            <span class="ref-tile__body">
              <span class="ref-tile__title">{{ link.title }}</span>
              <span class="ref-tile__subtitle">{{ link.subtitle }}</span>
            </span>
            <span class="ref-tile__chevron" aria-hidden="true">›</span>
          </a>
        }
      </div>
    </section>
  </div>
}
